import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import SEO from '../components/SEO/SEO';
import '../styles/pages/ContactPage.css';

const ContactPage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    budget: '',
    customization: [],
    message: ''
  });
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const serviceParam = params.get('service');
    const customizeParam = params.get('customize');

    if (serviceParam) {
      setFormData(prev => ({
        ...prev,
        service: serviceParam,
        message: `I'm interested in ${serviceParam.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} services.`
      }));
    }

    if (customizeParam === 'true') {
      setIsCustomizing(true);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCustomizationChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      customization: checked
        ? [...prev.customization, value]
        : prev.customization.filter(item => item !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formsubmit.co/ajax/info@figodigitalservice.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          service: formData.service || 'Not specified',
          budget: formData.budget || 'Not specified',
          customizations: formData.customization.length > 0 ? formData.customization.join(', ') : 'None',
          message: formData.message,
          _subject: `New Contact Form Submission${formData.service ? ` - ${formData.service}` : ''}`,
          _captcha: 'false',
          _template: 'table'
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We will get back to you soon.'
        });
        setFormData({
          name: '',
          email: '',
          service: '',
          budget: '',
          customization: [],
          message: ''
        });
        setTimeout(() => {
          setSubmitStatus({ type: '', message: '' });
        }, 5000);
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Oops! Something went wrong. Please try again or contact us directly at info@figodigitalservice.com'
      });
      setTimeout(() => {
        setSubmitStatus({ type: '', message: '' });
      }, 7000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const customizationOptions = [
    'Extended Support & Maintenance',
    'Additional Revisions',
    'Mobile App Development',
    'E-commerce Integration',
    'SEO Optimization',
    'Content Management System',
    'Analytics & Reporting',
    'Cloud Infrastructure Setup',
    'API Development',
    'Security Audit'
  ];

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us - FIGO Digital Services | Get a Free Consultation"
        description="Contact FIGO Digital Services for custom software development, AI automation, and digital transformation solutions. Free consultation available. Email: info@figodigitalservice.com | Phone: +234 703 291 4477"
        keywords="contact FIGO digital, software development quote, digital transformation consultation, custom software Nigeria, AI automation services, Abuja tech company contact, Lagos software development"
        url="https://figodigitalservices.com/contact"
      />
      <section className="contact-section">
        <div className="contact-container">
          <motion.div
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="contact-header">
              <h1>
                {isCustomizing ? 'Customize Your Package' : "Let's Build Something Amazing"}
              </h1>
              <p>
                {isCustomizing
                  ? 'Tell us your specific requirements and get a tailored quote.'
                  : 'Tell us about your project and we will create digital excellence together.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-grid">
                <div className="form-field">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Primary Service</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                  >
                    <option value="">Select a service</option>
                    <option value="brand">Brand & Identity</option>
                    <option value="software">Software Development</option>
                    <option value="ai">AI & Automation</option>
                    <option value="transform">Digital Transformation</option>
                    <option value="ux">UI/UX Design</option>
                    <option value="data">Data & Analytics</option>
                    <option value="marketing">Digital Marketing</option>
                    <option value="security">Cybersecurity</option>
                    <option value="hardware">Digital Hardware & Equipment</option>
                    <option value="consulting">IT Consulting & Training</option>
                    <option value="power">Power Solutions</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>Budget Range</label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                  >
                    <option value="">Select budget</option>
                    <option value="<1M">Less than ₦1M</option>
                    <option value="1-5M">₦1M - ₦5M</option>
                    <option value="5-20M">₦5M - ₦20M</option>
                    <option value="20-50M">₦20M - ₦50M</option>
                    <option value="50M+">Above ₦50M</option>
                  </select>
                </div>
              </div>

              {isCustomizing && (
                <div className="customization-section">
                  <label>Additional Services (Select all that apply)</label>
                  <div className="customization-grid">
                    {customizationOptions.map((item) => (
                      <label key={item} className="checkbox-label">
                        <input
                          type="checkbox"
                          value={item}
                          checked={formData.customization.includes(item)}
                          onChange={handleCustomizationChange}
                        />
                        <span>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-field">
                <label>{isCustomizing ? 'Specific Requirements *' : 'Project Details *'}</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder={isCustomizing
                    ? "Describe your specific requirements and any additional features you need..."
                    : "Tell us about your project..."}
                />
              </div>

              {submitStatus.message && (
                <motion.div
                  initial={{ y: -10 }}
                  animate={{ y: 0 }}
                  className={`status-message ${submitStatus.type}`}
                >
                  {submitStatus.type === 'success' ? '✓ ' : '✕ '}
                  {submitStatus.message}
                </motion.div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="submit-button"
              >
                {isSubmitting ? (
                  <span className="submit-button-content">
                    <span className="spinner"></span>
                    Sending...
                  </span>
                ) : (
                  isCustomizing ? 'Request Custom Quote' : 'Send Message'
                )}
              </button>
            </form>

            {/* Get In Touch Section */}
            <motion.div
              initial={{ y: 15 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="get-in-touch"
            >
              <div className="get-in-touch-header">
                <p className="get-in-touch-badge">Other Ways to Connect</p>
                <h2>Get In Touch</h2>
                <p>We're here to help. Reach out through any of these channels.</p>
              </div>

              <div className="contact-cards">
                {/* Email */}
                <div className="contact-card">
                  <div className="contact-card-icon">📧</div>
                  <h3>Email Us</h3>
                  <a href="mailto:info@figodigitalservice.com">info@figodigitalservice.com</a>
                </div>

                {/* Phone */}
                <div className="contact-card">
                  <div className="contact-card-icon">📞</div>
                  <h3>Call Us</h3>
                  <a href="tel:+2347032914477">+234 703 291 4477</a>
                  <br />
                  <a
                    href="https://wa.me/2347032914477"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-link"
                  >
                    <span>💬</span> WhatsApp
                  </a>
                </div>

                {/* Location */}
                <div className="contact-card">
                  <div className="contact-card-icon">📍</div>
                  <h3>Locations</h3>
                  <p>
                    Abuja, Nigeria<br />
                    Lagos, Nigeria<br />
                    <span style={{ opacity: 0.7 }}>Serving Globally</span>
                  </p>
                </div>

                {/* Office Hours */}
                <div className="contact-card">
                  <div className="contact-card-icon">⏰</div>
                  <h3>Office Hours</h3>
                  <p>
                    Monday - Friday<br />
                    9:00 AM - 6:00 PM<br />
                    <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>West Africa Time</span>
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
